import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { formatDate } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './transactions.css';
import { useSearchParams } from 'react-router-dom';
import { transactions } from '../utils/api';
import ViewSaleTransactionModal from '../components/ViewSaleTransactionModal';
import ViewPurchaseTransactionModal from '../components/ViewPurchaseTransactionModal';
import TransactionListTable from '../components/TransactionList/TransactionListTable';
import TransactionSummaryContent from '../components/TransactionList/TransactionSummaryContent';
import { TableProvider } from '../components/shared/table/context/TableContext';

// Helper component for summary cards with tooltips
const SummaryCard = ({ title, value, isCurrency = true, isPercentage = false }) => {
  // Format the exact value with more decimal places
  const exactValue = isCurrency
    ? `$${Number(Math.abs(value || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    : isPercentage
    ? `${Number(value || 0).toFixed(4)}%`
    : value;

  // Format the rounded value
  const roundedValue = isCurrency
    ? `$${Math.round(Math.abs(value || 0)).toLocaleString()}`
    : isPercentage
    ? `${Math.round(value || 0)}%`
    : value;

  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <Popover className="relative">
        <Popover.Button className="mt-1 text-2xl font-semibold text-gray-900 cursor-help focus:outline-none">
          {roundedValue}
        </Popover.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <Popover.Panel className="absolute z-10 px-2 py-1 -top-8 left-1/2 transform -translate-x-1/2 text-sm bg-gray-900 text-white rounded shadow-lg">
            {exactValue}
          </Popover.Panel>
        </Transition>
      </Popover>
    </div>
  );
};

const Transactions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('type') || 'sales');

  const [salesData, setSalesData] = useState({
    transactions: [],
    summary: { total_transactions: 0, total_sales: 0, total_fees: 0, average_sale: 0 },
    lastFetched: null,
  });
  const [purchasesData, setPurchasesData] = useState({
    transactions: [],
    summary: { total_transactions: 0, total_purchases: 0, average_amount: 0 },
    lastFetched: null,
  });
  const [loadingStates, setLoadingStates] = useState({
    sales: true,
    purchases: true
  });
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ column: 'transaction_date', order: 'DESC' });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [loadingTransaction, setLoadingTransaction] = useState(false);
  const debouncedFetch = useRef(null);
  const { user } = useAuth();

  // Update URL and trigger data fetch when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams(params => {
      params.set('type', tab);
      return params;
    });
  };

  const fetchTransactions = async (sortBy = sortConfig.column, sortOrder = sortConfig.order, type = activeTab) => {
    const dataState = type === 'sales' ? salesData : purchasesData;
    const setData = type === 'sales' ? setSalesData : setPurchasesData;

    const now = new Date().toISOString();
    if (dataState.lastFetched && (now - dataState.lastFetched < 5000)) {
      return;
    }
    
    try {
      setLoadingStates(prev => ({ ...prev, [type]: true }));
      const params = new URLSearchParams();
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      params.append('type', type);
      if (startDate) params.append('start_date', startDate.toISOString().split('T')[0]);
      if (endDate) params.append('end_date', endDate.toISOString().split('T')[0]);
      
      const userId = searchParams.get('userId');
      if (userId) params.append('userId', userId);
      
      const data = await transactions.getAll(params);
      if (type === 'sales') {
        setData({
          ...data,
          summary: {
            ...data.summary,
            average_sale: data.summary.average_amount
          },
          lastFetched: new Date().toISOString(),
        });
      } else {
        setData({ ...data, lastFetched: new Date().toISOString() });
      }
    } catch (err) {
      setError('Failed to load transactions');
      console.error('Error fetching transactions:', err);
    } finally {
      setLoadingStates(prev => ({ ...prev, [type]: false }));
    }
  };

  // Load transaction to view from URL parameter
  useEffect(() => {
    const viewTransactionId = searchParams.get('view');
    if (viewTransactionId) {
      const loadTransaction = async () => {
        try {
          setLoadingTransaction(true);
          const detailedTransaction = await transactions.getById(viewTransactionId);
          setSelectedTransaction(detailedTransaction);
        } catch (err) {
          console.error('Failed to fetch transaction details:', err);
          setError('Failed to load transaction details');
        } finally {
          setLoadingTransaction(false);
        }
      };
      loadTransaction();
    }
  }, [searchParams]);

  // Handle all data fetching in one effect
  useEffect(() => {
    const fetchData = () => {
      fetchTransactions(sortConfig.column, sortConfig.order, activeTab);
    };

    if (debouncedFetch.current) {
      clearTimeout(debouncedFetch.current);
    }

    debouncedFetch.current = setTimeout(fetchData, 300);

    return () => {
      if (debouncedFetch.current) {
        clearTimeout(debouncedFetch.current);
      }
    };
  }, [startDate, endDate, sortConfig, activeTab]);

  const handleSort = (column) => {
    setSortConfig(prevConfig => {
      const newOrder = prevConfig.column === column && prevConfig.order === 'ASC' ? 'DESC' : 'ASC';
      return { column, order: newOrder };
    });
  };

  const getSortIcon = (column) => {
    if (sortConfig.column !== column) return '⇅';
    return sortConfig.order === 'ASC' ? '↑' : '↓';
  };

  const handleDelete = async (transactionId) => {
    try {
      setDeletingId(transactionId);
      await transactions.delete(transactionId);
      
      if (activeTab === 'sales') {
        setSalesData(prev => {
          const updatedTransactions = prev.transactions.filter(t => t.id !== transactionId);
          const summary = {
            ...prev.summary,
            total_transactions: (prev.summary.total_transactions || 0) - 1,
            total_sales: (prev.summary.total_sales || 0) - (prev.transactions.find(t => t.id === transactionId)?.sale_price || 0),
            total_fees: (prev.summary.total_fees || 0) - (prev.transactions.find(t => t.id === transactionId)?.fees || 0),
            total_cost: (prev.summary.total_cost || 0) - (prev.transactions.find(t => t.id === transactionId)?.total_cost || 0),
            total_profit: (prev.summary.total_profit || 0) - (prev.transactions.find(t => t.id === transactionId)?.profit || 0)
          };
          summary.average_sale = summary.total_transactions ? summary.total_sales / summary.total_transactions : 0;
          summary.average_margin = summary.total_cost ? ((summary.total_profit / summary.total_cost) * 100) : 0;
          
          return {
            transactions: updatedTransactions,
            summary
          };
        });
      } else {
        setPurchasesData(prev => {
          const updatedTransactions = prev.transactions.filter(t => t.id !== transactionId);
          const summary = {
            ...prev.summary,
            total_transactions: (prev.summary.total_transactions || 0) - 1,
            total_purchases: (prev.summary.total_purchases || 0) - (prev.transactions.find(t => t.id === transactionId)?.purchase_price || 0)
          };
          summary.average_amount = summary.total_transactions ? summary.total_purchases / summary.total_transactions : 0;
          
          return {
            transactions: updatedTransactions,
            summary
          };
        });
      }
      
      setShowDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete transaction');
      console.error('Error deleting transaction:', err);
    } finally {
      setDeletingId(null);
    }
  };

  if (error) return <div className="p-4 text-red-600">{error}</div>;

  const { transactions: transactionList, summary } = activeTab === 'sales' ? salesData : purchasesData;

  return (
    <div className="container mx-auto p-2 sm:p-4 h-full flex flex-col">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold">Transaction History</h1>
          <div className="mt-4 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button
                  onClick={() => handleTabChange('sales')}
                  className={`
                    ${activeTab === 'sales'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  `}
                >
                  Sales
                </button>
                <button
                  onClick={() => handleTabChange('purchases')}
                  className={`
                    ${activeTab === 'purchases'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  `}
                >
                  Purchases
                </button>
            </nav>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex flex-col gap-2">
            <DatePicker
              selected={startDate}
              onChange={(dates) => {
                const [start, end] = dates;
                setStartDate(start);
                setEndDate(end);
              }}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              dateFormat="MM/dd/yyyy"
              placeholderText="Select date range"
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              autoComplete="off"
              isClearable
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              onChangeRaw={e => e.preventDefault()}
            />
          </div>
        </div>
      </div>

      <TableProvider 
        key={`transaction_${activeTab}`}
        initialState={{ 
          loading: loadingStates[activeTab], 
          error,
          filters: searchParams.get('contact_name') ? [{
            column: 'contact_name',
            operator: 'equals',
            value: searchParams.get('contact_name'),
            logicOperator: null
          }] : []
        }}
        onFiltersClear={() => {
          setSearchParams(params => {
            params.delete('contact_name');
            return params;
          });
        }}
        type={`transaction_${activeTab}`}
      >
        <TransactionSummaryContent 
          transactions={transactionList}
          activeTab={activeTab}
          SummaryCard={SummaryCard}
          onTransactionClick={async (id) => {
            try {
              setLoadingTransaction(true);
              const detailedTransaction = await transactions.getById(id);
              setSelectedTransaction(detailedTransaction);
            } catch (err) {
              console.error('Failed to fetch transaction details:', err);
              setError('Failed to load transaction details');
            } finally {
              setLoadingTransaction(false);
            }
          }}
        />
      </TableProvider>

      {/* View Transaction Modals */}
      {activeTab === 'sales' ? (
        <ViewSaleTransactionModal
          isOpen={selectedTransaction !== null}
          onClose={() => setSelectedTransaction(null)}
          transaction={selectedTransaction}
          onUpdate={async () => {
            try {
              const updatedTransaction = await transactions.getById(selectedTransaction.id);
              setSelectedTransaction(updatedTransaction);
              
              const sale_price = updatedTransaction.coins?.reduce((sum, coin) => sum + (coin.sale_price || 0), 0) || 0;
              const fees = updatedTransaction.coins?.reduce((sum, coin) => sum + (coin.fees || 0), 0) || 0;
              const net_amount = updatedTransaction.coins?.reduce((sum, coin) => sum + (coin.net_amount || 0), 0) || 0;
              const total_cost = updatedTransaction.coins?.reduce((sum, coin) => sum + (coin.total_cost || 0), 0) || 0;
              const profit = net_amount - total_cost;
              const margin_percentage = total_cost > 0 ? ((profit / total_cost) * 100).toFixed(2) : null;
              
              setSalesData(prev => ({
                ...prev,
                transactions: prev.transactions.map(t => {
                  if (t.id === selectedTransaction.id) {
                    return {
                      ...t,
                      transaction_date: updatedTransaction.transaction_date,
                      contact_id: updatedTransaction.contact_id,
                      contact_name: updatedTransaction.contact_name,
                      notes: updatedTransaction.notes,
                      sale_price,
                      fees,
                      net_amount,
                      total_cost,
                      profit,
                      margin_percentage
                    };
                  }
                  return t;
                })
              }));
            } catch (err) {
              console.error('Error updating transaction data:', err);
              setError('Failed to update transaction data');
            }
          }}
          handleDelete={async (id) => {
            await handleDelete(id);
            setSelectedTransaction(null);
          }}
        />
      ) : (
        <ViewPurchaseTransactionModal
          isOpen={selectedTransaction !== null}
          onClose={() => setSelectedTransaction(null)}
          transaction={selectedTransaction}
          onUpdate={async () => {
            try {
              const updatedTransaction = await transactions.getById(selectedTransaction.id);
              setSelectedTransaction(updatedTransaction);
              
              setPurchasesData(prev => ({
                ...prev,
                transactions: prev.transactions.map(t => {
                  if (t.id === selectedTransaction.id) {
                    return {
                      ...t,
                      transaction_date: updatedTransaction.transaction_date,
                      contact_id: updatedTransaction.contact_id,
                      contact_name: updatedTransaction.contact_name,
                      notes: updatedTransaction.notes,
                      purchase_price: updatedTransaction.coins?.reduce((sum, coin) => sum + (coin.purchase_price || 0), 0)
                    };
                  }
                  return t;
                })
              }));
            } catch (err) {
              console.error('Error updating transaction data:', err);
              setError('Failed to update transaction data');
            }
          }}
          handleDelete={async (id) => {
            await handleDelete(id);
            setSelectedTransaction(null);
          }}
        />
      )}
    </div>
  );
};

export default Transactions;
